export default `# Lifecycle Methods and Data Fetching

In previous chapters, you have learned about functional components and class components and discovered differences between the two.

Functional components – also called dumb components – are used mainly for presentational purposes. They can receive data,
but their main responsibility is to display data without applying any complex logic.

Class components are much smarter. They have a life cycle!

When a class component is instantiated, its constructor method is called first. Although the component is brought to life, it is still
not mounted on the DOM – it is not displayed yet. What happens next?

- The **componentWillMount** method is called. It fires shortly before a component appears on the screen. It's rarely used.
- The **render** method is responsible for rendering a component on screen and – as such – it has to be present in every class component.
- Once a component is rendered, React fires the **componentDidMount** method. This is a perfect moment to call the TMDB API for movies.

When a component gets new \`props\` or its \`state\` changes, the next four methods get called:
- **componentWillReceiveProps** - perfect place to react to changing data coming from either \`props\` or updated \`state\`
- **shouldComponentUpdate** - by default, a component updates always when it receives new \`props\` or its \`state\` changes. Here we can change this.
- **componentWillUpdate** is called just before an update
- **render** is called to re-render the component with new \`props\` or new \`state\`
- **componentDidUpdate** is called immediately after the component re-rendered

Finally, before a component is removed from the view, React triggers:
- **componentWillUnmount**, where you can perform some cleaning operations before your component disappears forever.

If a component triggers some errors it fires:
- the **componentDidCatch** method. This method is very helpful to create  *Error boundaries* components. If you wrap a regular component that is prone to trigger errors in the *Error  boundary* component, you could prevent the application from crashing.

In the "Sweet Pumpkins" application, we are going to create a \`Movies\` component and use the \`componentDidMount\` method to fetch a list of movies from TMDB API.

Before we start fetching data from the API, however, we need to understand the difference between two types of operations.

## Synchronous and Asynchronous operations

To request data from the TMDB API, we will use the \`fetch\` method. It accepts one argument – a URL from which we want to fetch data – and returns a *Promise*.
Actually, it accepts also another argument – \`options\`, but we are not going to use it here.

A \`Promise\` is a JavaScript object that is used to represent asynchronous operations.

There are two types of operations:
- **synchronous** - operations that happen one after another. If there are two tasks, the first has to finish before the second can start. The first one blocks the second one.
- **asynchronous** - the first operation doesn't block the second one. It means that the second operation can start before the first one finished its execution.

Fetching data from an external API is a good example of an asynchronous operation. If the fetching operation was synchronous, then the application would freeze until the remote server sent the data back.
But the \`fetch\` method is asynchronous. It returns a \`Promise\` object. That's why – although it takes time to fetch the data – the fetching process will not block the rest of the application.

The \`Promise\` object represents data that we may receive in the future. When you call \`fetch\`, a \`Promise\` object is created and its state is set to \`pending\`.
Later, when it receives data from an API, the \`Promise\` state changes to \`fulfilled\`. Or, if the request fails, the \`Promise\` state changes to \`rejected\`.

The \`Promise\` object has two methods: \`then\` and \`catch\`. In the \`then\` method, you can describe what should happen if a request succeeds (Promise gets fulfilled).
In the \`catch\` method, you can react to errors – when the request fails and the \`Promise\` gets rejected.
`;



// WEBPACK FOOTER //
// ./src/book/chapters/react-fetching-data.js